import { NotAuthorized } from "@/components/NotAuthorized";
import { Input } from "@/components/podkit/forms/Input";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast, useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3, Heading4, Subheading } from "@/components/podkit/typography/Headings";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { useCreateOrganizationInvite, useListSSOConfigurations, useOrganization, useOrganizationInvite, useUpdateOrganization } from "@/queries/organization-queries";
import { useMembership } from "@/hooks/use-membership";
import { joinOrganizationURL } from "@/routes/join-organization/join-organization";
import { formatError } from "@/utils/errors";
import { OrganizationRole, SSOConfiguration_ProviderType, SSOConfigurationState, type SSOConfiguration } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/flexkit/Button";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Text } from "@/components/podkit/typography/Text";
import { useSegmentTrack } from "@/hooks/use-segment";
import { TagsInput } from "@/components/podkit/forms/TagsInput";
import { Copy } from "lucide-react";
import { Check } from "lucide-react";
import { IconInfo } from "@/assets/icons/geist/IconInfo";

export const InviteMembersPage: FC = () => {
    useDocumentTitle("Invite Members");

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();
    const { membership, isPending: isLoadingMembership } = useMembership();

    if (!organization && isLoadingOrganization) {
        return <LoadingState />;
    }

    if (!membership && isLoadingMembership) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return (
            <NotAuthorized description="You do not have sufficient permissions to invite others to this organization. Contact your organization's admin to grant those permissions." />
        );
    }

    return (
        <div className="flex flex-col gap-6 pt-6">
            <InviteMembers />
            <Text className="text-xs text-content-primary">
                If you would like to invite people by email domain, you can set that up in{" "}
                <Link className="font-bold underline" to="/settings/manage-organization">
                    Manage Organization
                </Link>
                .
            </Text>
        </div>
    );
};

const isCustom = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.CUSTOM;
const isBuiltin = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.BUILTIN;

export const InviteMembers: FC = () => {
    const { data: ssoConfigs } = useListSSOConfigurations();
    const defaultConfigs = ssoConfigs?.filter(isBuiltin) || [];
    const customConfig = ssoConfigs ? ssoConfigs.find(isCustom) : undefined;
    
    const customSSOEnabled = customConfig?.state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE;
    const defaultSSOEnabled = defaultConfigs.some(
        (config) => config.state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE
    );

    return (
        <div className="space-y-6">
           {customSSOEnabled && (
                <div className="flex items-center gap-2 rounded-md bg-gray-50 p-3 border border-border-light">
                    <IconInfo size="sm" />
                    <div>
                        <Text className="text-base font-bold">Your organization has SSO enabled</Text>
                        <Text className="text-base">Log in user authentication is managed through your identity provider.</Text>
                    </div>
                </div>
            )}
            <InviteMembersByLink />
            {defaultSSOEnabled && (
                <>
                    <InviteMembersByEmailAddress />
                    <InviteMembersByEmailDomain />
                </>
            )}
        </div>
    );
};

const InviteMembersByLink: FC = () => {
    const { data: invite } = useOrganizationInvite();
    const createOrganizationInvite = useCreateOrganizationInvite();
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const inviteURL = useMemo(() => {
        return joinOrganizationURL(invite?.inviteId || "");
    }, [invite?.inviteId]);

    const handleCopyToClipboard = useCallback(() => {
        if (!invite?.inviteId) {
            return;
        }

        void navigator.clipboard.writeText(inviteURL).then(() => {
            setCopied(true);
        });
    }, [setCopied, invite?.inviteId, inviteURL]);

    return (
        <div className="space-y-3">
            <div>
                <Heading4>Invite link</Heading4>
                <Subheading>Share this link with others you&apos;d like to join your organization.</Subheading>
            </div>

            <div className="gap-6 pt-1">
                <Input className="pr-12 text-stone-400" readOnly value={inviteURL} />
            </div>

            <div className="inline-flex items-start justify-start gap-3">
                <Button
                    variant="secondary"
                    onClick={handleCopyToClipboard}
                    LeadingIcon={() => (copied ? <Check size={20} /> : <Copy size={20} />)}
                >
                    Copy Link
                </Button>
                <Button
                    variant="secondary"
                    onClick={() => {
                        createOrganizationInvite.mutate(void 0, {
                            onSuccess: () => {
                                toast({ title: `Invite link reset` });
                            },
                            onError: (error) => {
                                toast({ title: `Failed to reset invite link`, description: formatError(error) });
                            },
                        });
                    }}
                >
                    Reset Invite Link
                </Button>
            </div>
        </div>
    );
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteMembersByEmailAddress: FC = () => {
    const track = useSegmentTrack();
    const { data: invite } = useOrganizationInvite();
    const { data: organization } = useOrganization();
    const [emails, setEmails] = useState<string[]>([]);

    const inviteURL = joinOrganizationURL(invite?.inviteId || "");
    const organizationName = organization?.name || "";

    const handleInviteUsers = useCallback(
        (evt: React.FormEvent<HTMLFormElement>) => {
            evt.preventDefault();
            if (emails.length === 0 || !invite) return;

            emails.forEach((email) => {
                track("organization_email_invited", {
                    email: email,
                    invite_link: inviteURL,
                    organization_name: organizationName,
                });
            });

            setEmails([]);
            toast({
                title: `Invitation${emails.length > 1 ? "s" : ""} sent successfully.`,
            });
        },
        [emails, invite, inviteURL, organizationName, track],
    );

    const validateEmail = useCallback(
        (email: string) => {
            if (emails.includes(email)) {
                return false;
            }
            return emailRegex.test(email);
        },
        [emails],
    );

    return (
        <div className="space-y-3">
            <div>
                <Heading4>Add people by email address</Heading4>
                <Subheading>Invite your team members to your organization</Subheading>
            </div>
            <TagsInput
                tags={emails}
                placeHolder="email@example.com"
                validateInputFn={validateEmail}
                onAddValue={(email) => setEmails((prev) => [...prev, email])}
                onRemoveTag={(email) => setEmails((prev) => prev.filter((e) => e !== email))}
            />
            <form onSubmit={handleInviteUsers}>
                <Button
                    variant="secondary"
                    className="mt-3"
                    type="submit"
                    disabled={emails.length === 0}
                    LeadingIcon={IconPlus}
                >
                    Send invite
                </Button>
            </form>
        </div>
    );
};

const InviteMembersByEmailDomain: FC = () => {
    const { toast } = useToast();

    const { data: orgData } = useOrganization();
    const inviteDomains = useMemo(() => orgData?.inviteDomains?.domains || [], [orgData]);
    const updateOrganization = useUpdateOrganization();

    const doUpdateInviteDomains = useCallback(
        async (inviteDomains: string[]) => {
            if (!orgData) {
                return;
            }

            try {
                await updateOrganization.mutateAsync({ inviteDomains });
                toast({
                    title: "Updated allowed email domains",
                });
            } catch (err) {
                toast({
                    title: "Failed to update organization's allowed email domains",
                    description: formatError(err),
                });
                throw err;
            }
        },
        [orgData, updateOrganization, toast],
    );

    const handleAddDomain = useCallback(
        async (newDomain: string) => {
            await doUpdateInviteDomains([...inviteDomains, newDomain]);
        },
        [doUpdateInviteDomains, inviteDomains],
    );

    const handleRemoveDomain = useCallback(
        async (removeDomain: string) => {
            await doUpdateInviteDomains(inviteDomains.filter((domain) => domain !== removeDomain));
        },
        [doUpdateInviteDomains, inviteDomains],
    );

    return (
        <div className="flex flex-col gap-3">
            <div>
                <Heading3>Allowed email domains</Heading3>
                <Subheading>Enter an email domain of an existing member of this organization.</Subheading>
            </div>

            <TagsInput
                tags={inviteDomains}
                onAddValue={handleAddDomain}
                onRemoveTag={handleRemoveDomain}
                placeHolder="yourcompany.com"
            />
        </div>
    );
};
